import { MutationCache, QueryCache } from "@tanstack/react-query";
import { RateLimitError, UnauthenticatedError, redirectToLogin } from "./api.js";
import { emitRateLimited } from "./rateLimitNotice.js";

/**
 * GT-FI-MMP-COMPLETION-01 Wave 1 — global mid-session 401 handler.
 *
 * Before this module, the SessionProvider's initial `/v1/auth/session`
 * fetch was the only place the SPA caught a 401 and redirected to
 * Keycloak login. Every other API call would `throw` an
 * `UnauthenticatedError` (per `lib/api.ts` contract), the calling
 * react-query hook would expose it as an error state, and the UI
 * would render the raw `"[401] …/v1/insights/intents: unauthenticated"`
 * message to the operator instead of redirecting.
 *
 * That gap caused the Owner-reported Fleet Intelligence finding: the
 * `/insights` page rendered (because the session was valid at
 * page-load), the access token then expired (or the inline refresh
 * grant failed), and the next API call surfaced a raw 401 instead of
 * a silent re-login. The Owner's expectation is "I am logged in →
 * Insights works"; the surfaced 401 broke that expectation.
 *
 * This module exports a QueryClient builder that wires
 * UnauthenticatedError detection into both the query and mutation
 * caches. Any `apiGet`/`apiSend`/`apiDownload` call that returns 401
 * triggers `redirectToLogin()` — exactly what `lib/api.ts` documents
 * callers must do. The handler is idempotent (one redirect per page
 * load) so multiple concurrent failing queries do not loop.
 *
 * Tests: `apps/web/src/lib/unauthenticatedHandler.test.ts`.
 */

let redirectInFlight = false;

/**
 * Test-only reset for unit tests that exercise the handler more than
 * once. Production code should never call this.
 */
export function __resetUnauthenticatedRedirectForTests(): void {
  redirectInFlight = false;
}

function onCacheError(err: unknown): void {
  // GT-…-RATE-LIMIT-FIX-02 — surface a single controlled notice on 429
  // (debounced inside `emitRateLimited`). This is NOT a retry — the
  // QueryClient retry policy already decides whether/when to retry; this
  // only tells the operator what happened instead of a raw "[429] …".
  if (err instanceof RateLimitError) {
    emitRateLimited(err.retryAfterSeconds);
    return;
  }
  if (!(err instanceof UnauthenticatedError)) return;
  if (redirectInFlight) return;
  redirectInFlight = true;
  redirectToLogin();
}

/**
 * Builds a `QueryCache` whose `onError` callback redirects to login
 * on any `UnauthenticatedError`. Pass this into the QueryClient
 * constructor to install the safety net.
 *
 * Important: this handler is **safe for read-during-load** — the
 * SessionProvider's own `fetchSession` call lives outside react-query
 * (it uses `apiGet` directly and converts 401 to `null`), so its
 * fast-path redirect remains the primary trigger on initial load.
 * This handler is the safety net for queries that fire *after* the
 * SPA has mounted.
 */
export function buildUnauthenticatedAwareQueryCache(): QueryCache {
  return new QueryCache({
    onError: (error) => onCacheError(error),
  });
}

/** Mirror of {@link buildUnauthenticatedAwareQueryCache} for mutations. */
export function buildUnauthenticatedAwareMutationCache(): MutationCache {
  return new MutationCache({
    onError: (error) => onCacheError(error),
  });
}
